import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Loader2, Trash2, Clock, User as UserIcon, X } from "lucide-react";
import { toast } from "sonner";
import { platformCalendarApi } from "../../lib/api";
import { Modal, DInput, DSelect, DTextarea } from "./ui";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_START_HOUR = 6;
const WEEK_END_HOUR = 21; // 9pm
const SLOT_MIN = 30;
const SLOT_PX = 26;

const KIND_STYLE = {
  demo: { bg: "bg-blue-500/85", border: "border-blue-400/60", label: "Demo", dot: "bg-blue-400" },
  crm: { bg: "bg-violet-500/85", border: "border-violet-400/60", label: "CRM meeting", dot: "bg-violet-400" },
  event: { bg: "bg-emerald-500/85", border: "border-emerald-400/60", label: "Event", dot: "bg-emerald-400" },
  task: { bg: "bg-amber-500/85", border: "border-amber-400/60", label: "Task", dot: "bg-amber-400" },
};

function pad2(n) { return String(n).padStart(2, "0"); }
function fmtISODate(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function toMinutes(hhmm) { const [h, m] = (hhmm || "0:0").split(":").map(Number); return h * 60 + m; }
function minutesToHHMM(mins) { return `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`; }
function fmtTimeLabel(hhmm) {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${ampm}`;
}

export default function PlatformCalendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [appointments, setAppointments] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createFor, setCreateFor] = useState(null); // {date, start, end} | null
  const [detail, setDetail] = useState(null); // block | null

  const { rangeStart, rangeEnd, gridDays } = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(cursor);
      const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
      return { rangeStart: s, rangeEnd: addDays(s, 7), gridDays: days };
    }
    const monthStart = startOfMonth(cursor);
    const gridStart = startOfWeek(monthStart);
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return { rangeStart: gridStart, rangeEnd: addDays(gridStart, 42), gridDays: days };
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    const s = fmtISODate(rangeStart), e = fmtISODate(rangeEnd);
    try {
      const [appts, ents] = await Promise.all([
        platformCalendarApi.appointments(s, e),
        platformCalendarApi.entries(s, e),
      ]);
      setAppointments(appts || []);
      setEntries(ents || []);
    } catch (_) {
      toast.error("Failed to load calendar data.");
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  const blocks = useMemo(() => {
    const a = appointments.map((x) => ({ ...x, deletable: false }));
    const b = entries.map((x) => ({ ...x, kind: x.type, deletable: true }));
    return [...a, ...b];
  }, [appointments, entries]);

  const blocksByDate = useMemo(() => {
    const m = {};
    for (const b of blocks) { (m[b.date] = m[b.date] || []).push(b); }
    for (const k in m) m[k].sort((x, y) => toMinutes(x.start) - toMinutes(y.start));
    return m;
  }, [blocks]);

  const goToday = () => setCursor(new Date());
  const goPrev = () => setCursor((c) => (view === "week" ? addDays(c, -7) : new Date(c.getFullYear(), c.getMonth() - 1, 1)));
  const goNext = () => setCursor((c) => (view === "week" ? addDays(c, 7) : new Date(c.getFullYear(), c.getMonth() + 1, 1)));

  const openCreate = (dateStr, startHHMM) => {
    const start = startHHMM || "09:00";
    setCreateFor({ date: dateStr, start, end: minutesToHHMM(toMinutes(start) + 30) });
  };

  const handleCreated = async () => {
    setCreateFor(null);
    await load();
  };

  const handleDeleted = async (id) => {
    try {
      await platformCalendarApi.deleteEntry(id);
      toast.success("Deleted.");
      setDetail(null);
      await load();
    } catch (_) {
      toast.error("Failed to delete.");
    }
  };

  const today = fmtISODate(new Date());
  const label = view === "week"
    ? `${gridDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${gridDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
    : cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div data-testid="platform-calendar-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={goPrev} className="p-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="cal-next" onClick={goNext} className="p-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="cal-today" onClick={goToday} className="px-3 py-2 rounded-lg border border-white/10 text-[12.5px] text-slate-300 hover:bg-white/5">Today</button>
          <div className="text-[15px] font-bold text-white ml-2">{label}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-white/5 p-1 border border-white/10">
            {["month", "week"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`px-3.5 py-1.5 rounded-md text-[12.5px] font-medium capitalize transition-colors ${view === v ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400 hover:text-slate-200"}`}>
                {v}
              </button>
            ))}
          </div>
          <button data-testid="cal-new-entry-btn" onClick={() => openCreate(today, "09:00")}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold">
            <Plus className="h-4 w-4" /> New
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-3 text-[11.5px] text-slate-400">
        {Object.entries(KIND_STYLE).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}</span>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>
      ) : view === "month" ? (
        <MonthGrid gridDays={gridDays} cursor={cursor} today={today} blocksByDate={blocksByDate} onSlotClick={openCreate} onBlockClick={setDetail} />
      ) : (
        <WeekGrid gridDays={gridDays} today={today} blocksByDate={blocksByDate} onSlotClick={openCreate} onBlockClick={setDetail} />
      )}

      {createFor && (
        <CreateEntryModal initial={createFor} onClose={() => setCreateFor(null)} onSaved={handleCreated} />
      )}
      {detail && (
        <DetailModal block={detail} onClose={() => setDetail(null)} onDelete={handleDeleted} />
      )}
    </div>
  );
}

function MonthGrid({ gridDays, cursor, today, blocksByDate, onSlotClick, onBlockClick }) {
  const curMonth = cursor.getMonth();
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 border-b border-white/10">
        {DAY_NAMES.map((d) => (
          <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {gridDays.map((d) => {
          const dateStr = fmtISODate(d);
          const dayBlocks = blocksByDate[dateStr] || [];
          const isOtherMonth = d.getMonth() !== curMonth;
          const isToday = dateStr === today;
          return (
            <div
              key={dateStr}
              data-testid={`cal-day-${dateStr}`}
              onClick={() => onSlotClick(dateStr, "09:00")}
              className={`min-h-[104px] border-b border-r border-white/5 p-1.5 cursor-pointer hover:bg-white/[0.03] transition-colors ${isOtherMonth ? "opacity-40" : ""}`}
            >
              <div className={`text-[11.5px] font-semibold mb-1 inline-flex items-center justify-center h-5 w-5 rounded-full ${isToday ? "bg-cyan-500 text-white" : "text-slate-400"}`}>{d.getDate()}</div>
              <div className="space-y-1 max-h-[76px] overflow-y-auto">
                {dayBlocks.map((b) => {
                  const s = KIND_STYLE[b.kind] || KIND_STYLE.event;
                  return (
                    <div
                      key={b.id}
                      data-testid={`cal-block-${b.id}`}
                      onClick={(e) => { e.stopPropagation(); onBlockClick(b); }}
                      className={`${s.bg} text-white text-[10.5px] font-semibold rounded px-1.5 py-0.5 truncate leading-tight`}
                      title={`${fmtTimeLabel(b.start)} — ${b.title}`}
                    >
                      {fmtTimeLabel(b.start)} {b.title}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ gridDays, today, blocksByDate, onSlotClick, onBlockClick }) {
  const totalSlots = ((WEEK_END_HOUR - WEEK_START_HOUR) * 60) / SLOT_MIN;
  const gridHeight = totalSlots * SLOT_PX;
  const hourMarks = Array.from({ length: WEEK_END_HOUR - WEEK_START_HOUR }, (_, i) => WEEK_START_HOUR + i);

  const topFor = (hhmm) => {
    const mins = Math.max(WEEK_START_HOUR * 60, Math.min(WEEK_END_HOUR * 60, toMinutes(hhmm)));
    return ((mins - WEEK_START_HOUR * 60) / SLOT_MIN) * SLOT_PX;
  };
  const heightFor = (start, end) => Math.max(SLOT_PX - 2, topFor(end) - topFor(start));

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="cal-week-grid">
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div />
        {gridDays.map((d) => {
          const dateStr = fmtISODate(d);
          const isToday = dateStr === today;
          return (
            <div key={dateStr} className="px-2 py-2 text-center border-b border-l border-white/10">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{DAY_NAMES[d.getDay()]}</div>
              <div className={`text-[13px] font-bold mt-0.5 inline-flex items-center justify-center h-6 w-6 rounded-full ${isToday ? "bg-cyan-500 text-white" : "text-slate-200"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="grid overflow-y-auto" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", maxHeight: 560 }}>
        <div className="relative" style={{ height: gridHeight }}>
          {hourMarks.map((h) => (
            <div key={h} className="absolute right-2 -translate-y-2 text-[10px] text-slate-500" style={{ top: ((h - WEEK_START_HOUR) * 60 / SLOT_MIN) * SLOT_PX }}>
              {fmtTimeLabel(`${pad2(h)}:00`)}
            </div>
          ))}
        </div>
        {gridDays.map((d) => {
          const dateStr = fmtISODate(d);
          const dayBlocks = blocksByDate[dateStr] || [];
          const slots = Array.from({ length: totalSlots }, (_, i) => WEEK_START_HOUR * 60 + i * SLOT_MIN);
          return (
            <div key={dateStr} className="relative border-l border-white/10" style={{ height: gridHeight }}>
              {slots.map((mins) => (
                <button
                  key={mins}
                  data-testid={`cal-slot-${dateStr}-${minutesToHHMM(mins)}`}
                  onClick={() => onSlotClick(dateStr, minutesToHHMM(mins))}
                  className="absolute left-0 right-0 border-b border-white/[0.04] hover:bg-white/[0.06]"
                  style={{ top: ((mins - WEEK_START_HOUR * 60) / SLOT_MIN) * SLOT_PX, height: SLOT_PX }}
                />
              ))}
              {dayBlocks.map((b) => {
                const s = KIND_STYLE[b.kind] || KIND_STYLE.event;
                return (
                  <div
                    key={b.id}
                    data-testid={`cal-block-${b.id}`}
                    onClick={(e) => { e.stopPropagation(); onBlockClick(b); }}
                    className={`absolute left-0.5 right-0.5 ${s.bg} border ${s.border} text-white rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold overflow-hidden cursor-pointer shadow`}
                    style={{ top: topFor(b.start), height: heightFor(b.start, b.end) }}
                    title={`${fmtTimeLabel(b.start)}–${fmtTimeLabel(b.end)} · ${b.title}`}
                  >
                    <div className="truncate">{b.title}</div>
                    <div className="truncate opacity-80 text-[9.5px]">{fmtTimeLabel(b.start)}–{fmtTimeLabel(b.end)}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CreateEntryModal({ initial, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("event");
  const [date, setDate] = useState(initial.date);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required."); return; }
    if (end <= start) { toast.error("End time must be after start time."); return; }
    setSaving(true);
    try {
      await platformCalendarApi.createEntry({ title: title.trim(), type, date, start, end, notes });
      toast.success(`${type === "task" ? "Task" : "Event"} created.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create entry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New calendar entry" onClose={onClose} testid="cal-create-modal">
      <form onSubmit={submit} className="space-y-4">
        <DInput label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required testid="cal-create-title" placeholder="e.g. Team sync" />
        <DSelect label="Type" value={type} onChange={(e) => setType(e.target.value)} testid="cal-create-type" options={[["event", "Event"], ["task", "Task"]]} />
        <DInput label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required testid="cal-create-date" />
        <div className="grid grid-cols-2 gap-3">
          <DInput label="Start time" type="time" value={start} onChange={(e) => setStart(e.target.value)} required testid="cal-create-start" />
          <DInput label="End time" type="time" value={end} onChange={(e) => setEnd(e.target.value)} required testid="cal-create-end" />
        </div>
        <DTextarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} testid="cal-create-notes" placeholder="Optional notes…" />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="cal-create-cancel" className="px-4 py-2 rounded-lg border border-white/10 text-[13px] text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cal-create-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DetailModal({ block, onClose, onDelete }) {
  const s = KIND_STYLE[block.kind] || KIND_STYLE.event;
  const [confirming, setConfirming] = useState(false);
  return (
    <Modal title={s.label} onClose={onClose} testid="cal-detail-modal">
      <div className="space-y-3">
        <div className="text-[16px] font-bold text-white" data-testid="cal-detail-title">{block.title}</div>
        <div className="flex items-center gap-2 text-[13px] text-slate-300">
          <Clock className="h-4 w-4 text-slate-500" />
          {block.date} · {fmtTimeLabel(block.start)} – {fmtTimeLabel(block.end)}
        </div>
        {block.contactName && (
          <div className="flex items-center gap-2 text-[13px] text-slate-300">
            <UserIcon className="h-4 w-4 text-slate-500" />
            {block.contactName}{block.contactEmail ? ` · ${block.contactEmail}` : ""}
          </div>
        )}
        {block.notes && (
          <div className="text-[12.5px] text-slate-400 bg-white/[0.03] border border-white/10 rounded-lg p-3 whitespace-pre-wrap">{block.notes}</div>
        )}
        {!block.deletable && (
          <div className="text-[11.5px] text-slate-500 italic">View-only — sourced from {block.kind === "demo" ? "Demo Booking" : "the CRM pipeline"}.</div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} data-testid="cal-detail-close" className="px-4 py-2 rounded-lg border border-white/10 text-[13px] text-slate-300 hover:bg-white/5">Close</button>
          {block.deletable && (
            confirming ? (
              <button onClick={() => onDelete(block.id)} data-testid="cal-detail-confirm-delete" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[13px] font-semibold">
                <Trash2 className="h-3.5 w-3.5" /> Confirm delete
              </button>
            ) : (
              <button onClick={() => setConfirming(true)} data-testid="cal-detail-delete" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-[13px] font-semibold">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )
          )}
        </div>
      </div>
    </Modal>
  );
}
